/** Opleider-team uitnodiging accepteren (migratie 150): wachtwoord zetten → cockpit (rol bepaalt startscherm). */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button } from '../components';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';
import { toast } from 'react-hot-toast';

export const OpleiderInvitePage: React.FC = () => {
  const { t } = useTranslation('employer');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<{ email: string; first_name: string; last_name: string; company_name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');

  useEffect(() => {
    if (!token) {
      setError(t('invite.invalidLink'));
      setLoading(false);
      return;
    }
    api
      .get(`/auth/invite/opleider/${token}`)
      .then((res) => {
        if (res.data.success && res.data.data) {
          setInviteInfo(res.data.data);
        } else {
          setError(t('invite.expiredLink'));
        }
      })
      .catch(() => {
        setError(t('invite.expiredLink'));
      })
      .finally(() => setLoading(false));
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !password || !passwordConfirm) {
      toast.error(t('invite.fillAllFields'));
      return;
    }
    if (password.length < 8) {
      toast.error(t('invite.passwordTooShort'));
      return;
    }
    if (password !== passwordConfirm) {
      toast.error(t('invite.passwordMismatch'));
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post(`/auth/invite/opleider/${token}`, { password });
      if (res.data.success && res.data.data?.user && res.data.data?.token) {
        login(res.data.data.user, res.data.data.token);
        toast.success(t('invite.activateSuccess'));
        navigate('/opleider/academy/trainingen', { replace: true });
      } else {
        toast.error(t('invite.errorGeneric'));
      }
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message || t('invite.setPasswordError'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('invite.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !inviteInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <Card className="max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('invite.invalidLink')}</h1>
          <p className="text-gray-600 mb-6">{error || t('invite.expiredLink')}</p>
          <Button onClick={() => navigate('/login')}>{t('invite.goToLogin')}</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="max-w-md w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('invite.activateTitle')}</h1>
        <p className="text-gray-600 mb-6">
          {t('invite.activateDesc', { company: inviteInfo.company_name })}
        </p>
        <p className="text-sm text-gray-500 mb-4">
          {t('invite.emailLabel')}: {inviteInfo.email}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('invite.passwordLabel')}</label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('invite.passwordPlaceholder')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('invite.confirmPasswordLabel')}</label>
            <input
              type="password"
              required
              minLength={8}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('invite.confirmPasswordPlaceholder')}
            />
          </div>
          <Button type="submit" className="w-full" loading={submitting}>
            {t('invite.activateButton')}
          </Button>
        </form>
      </Card>
    </div>
  );
};
